import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import confetti from "canvas-confetti";
import { Sparkles, X, Share2, Star, ArrowRight } from "lucide-react";

/** Project completion celebration banner — shown once on the client overview
 *  when their project is freshly marked complete. Fires confetti, suggests
 *  testimonial + share, then dismissable. */
export default function CompletionCelebration({ projectId }) {
  const [data, setData] = useState(null);
  const [shareCard, setShareCard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadCelebration = async (pid) => {
      try {
        const r = await api.get(`/projects/${pid}/celebration`);
        if (!cancelled && r.data?.show) {
          setData(r.data);
          setTimeout(() => burstConfetti(), 250);
        }
      } catch (err) {
        console.warn("Celebration check failed:", err);
      }
    };
    if (!projectId) {
      api.get("/projects/my")
        .then((r) => {
          if (cancelled) return;
          const p = (r.data || []).find((pj) => ["completed", "installed"].includes(pj.status) && !pj.completion_seen_by_client);
          if (p) loadCelebration(p.project_id);
        })
        .catch((err) => console.warn("Projects fetch failed:", err));
    } else {
      loadCelebration(projectId);
    }
    return () => { cancelled = true; };
  }, [projectId]);

  const dismiss = async () => {
    if (!data) return;
    try {
      await api.post(`/projects/${data.project_id}/celebration/seen`);
    } catch (err) {
      console.warn("Celebration dismiss failed:", err);
    }
    setData(null);
  };

  const openShare = async () => {
    const r = await api.get(`/projects/${data.project_id}/share-card`);
    setShareCard(r.data);
  };

  if (!data) return null;
  const cover = data.before_after?.after || data.hero_photo;

  return (
    <>
      <div className="card-pb p-0 overflow-hidden border-2 border-[#C9A356] relative" data-testid="celebration-banner">
        {/* Cover image */}
        {cover && (
          <div className="relative h-48 sm:h-64 overflow-hidden">
            <img src={cover} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/40 to-transparent"></div>
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 w-8 h-8 grid place-items-center bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white"
              data-testid="dismiss-celebration"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-4 left-5 text-white">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#C9A356]" />
                <span className="overline text-[#C9A356]">It's official</span>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl drop-shadow">
                {data.project_title} is complete!
              </h2>
            </div>
          </div>
        )}
        {!cover && (
          <div className="px-6 py-8 bg-gradient-to-br from-[#0A2540] to-[#103960] text-white">
            <Sparkles className="w-7 h-7 text-[#C9A356] mb-2" />
            <h2 className="font-heading text-2xl">{data.project_title} is complete!</h2>
          </div>
        )}

        <div className="p-6">
          <p className="text-sm text-[#475569] dark:text-slate-400">
            Thanks for trusting us with your project on Sint Maarten. Show off the work — your friends should see what's possible.
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={openShare} className="btn-accent text-sm" data-testid="celebration-share">
              <Share2 className="w-4 h-4 mr-1" /> Share your project
            </button>
            <Link to={`/dashboard/projects?id=${data.project_id}`} className="btn-outline-navy text-sm" data-testid="celebration-view">
              View timeline <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
            <button onClick={dismiss} className="text-sm text-[#475569] dark:text-slate-400 hover:text-[#0A2540] px-3 py-2">
              Maybe later
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-[#E2E8F0] dark:border-slate-700 flex items-center gap-2 text-xs text-[#475569] dark:text-slate-400">
            <Star className="w-3.5 h-3.5 text-[#C9A356]" />
            Loved the work? Leave a quick review — clients trust other clients.
          </div>
        </div>
      </div>

      {shareCard && <ShareCardModal card={shareCard} onClose={() => setShareCard(null)} />}
    </>
  );
}

function burstConfetti() {
  // Multi-burst gold + navy + emerald for ~2s
  const colors = ["#C9A356", "#0A2540", "#10B981", "#FBBF24"];
  const end = Date.now() + 1500;
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Big center burst
  confetti({ particleCount: 80, spread: 90, origin: { y: 0.4 }, colors });
}

function ShareCardModal({ card, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-[#0A2540] to-[#103960] p-6 text-white">
          <Sparkles className="w-6 h-6 text-[#C9A356] mb-2" />
          <h3 className="font-heading text-xl">Share your project</h3>
        </div>
        <div className="p-5">
          {card.first_photo_url && <img src={card.first_photo_url} alt="" className="w-full h-40 object-cover rounded-md mb-3" />}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 text-sm whitespace-pre-wrap">{card.text}</div>
          <div className="flex flex-wrap gap-2 mt-4">
            <a href={card.whatsapp_url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm text-white">WhatsApp</a>
            <a href={card.facebook_url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-sm text-white">Facebook</a>
            <a href={card.twitter_url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-slate-800 hover:bg-slate-900 rounded-md text-sm text-white">X</a>
          </div>
        </div>
      </div>
    </div>
  );
}
